// The shared YouTube-metadata cleaner — single rule-set, mirrored verbatim
// into the desktop module, the guest PWA service, and the cache script.
//
// PHILOSOPHY (user rulings 2026-08-21):
//   - Strip PRESENTATION junk only: HTML entities, video-descriptors
//     ((Official Video), (Lyrics), [HD]…), channel-name artists, feat blocks
//     in titles, emoji, promo tails, standalone year tags.
//   - KEEP anything describing the actual recording: (Remix), (Radio Edit),
//     (Live…), (Acoustic), (Clean Version), (Slowed + Reverb), (Sped Up),
//     (Bass Boosted), (8D Audio), (Nightcore) — the file IS that version.

const KEEP_WORDS = /remix|edit|live|acoustic|version|cover|instrumental|slowed|sped|reverb|bass boost|8d|nightcore|mashup|bootleg|clean|dirty|demo|session|unplugged|extended|vip|dub|refix|freestyle|prod/i;
const JUNK_WORDS = /official|video|audio|lyric|lyrics|visuali[sz]er|full hd|\bhd\b|\bhq\b|\b4k\b|\b8k\b|remaster|\bmv\b|m\/v|out now|full song|sub espa|subtitul|letra\b|legendado|paroles|color coded|shorts|premiere|new video|exclusive|explicit|parental advisory/i;

const decodeEntities = (s) => String(s || "")
  .replace(/&amp;/gi, "&").replace(/&#0?39;/g, "'").replace(/&apos;/gi, "'")
  .replace(/&quot;/gi, '"').replace(/&lt;/gi, "<").replace(/&gt;/gi, ">")
  .replace(/&nbsp;/gi, " ")
  .replace(/&#(\d+);/g, (_, n) => { try { return String.fromCodePoint(Number(n)); } catch (_) { return " "; } });

const stripDecorations = (s) => String(s || "")
  .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{2B00}-\u{2BFF}]/gu, " ") // emoji + symbols
  .replace(/[★☆♪♫✦✧•]+/g, " ")
  .replace(/#[\w]+/g, " ")            // hashtags (#shorts and friends)
  .replace(/\s+/g, " ").trim();

// Bracketed groups: strip only when they read as VIDEO junk and carry no
// recording-version language. "(Clean Version)" survives (version);
// "(Official Video)" dies; "(Live at Wembley)" is never touched.
const stripJunkBrackets = (s) => String(s || "").replace(
  /[([{]([^()[\]{}]*)[)\]}]/g,
  (whole, inner) => {
    const t = String(inner || "").trim();
    if (!t) return " ";
    if (/^(19|20)\d{2}$/.test(t)) return " ";            // standalone year tag
    if (KEEP_WORDS.test(t)) return whole;                 // recording info stays
    if (JUNK_WORDS.test(t)) return " ";                   // video junk goes
    return whole;                                         // unknown → leave it
  }
);

// Feat blocks come out of TITLES only — the artist string keeps its credits.
const stripTitleFeat = (s) => String(s || "")
  .replace(/[([{]\s*(?:feat|ft)\.?\s[^)\]}]*[)\]}]/gi, " ")
  .replace(/\s+(?:feat\.?|ft\.?|featuring)\s+.*$/i, " ");

const stripTails = (s) => String(s || "")
  .replace(/\s*[|/]+\s*(official.*|lyrics?.*|audio.*|video.*)$/i, " ")
  .replace(/\s*-\s*youtube\s*$/i, " ")
  .replace(/\s+(official\s*(video|audio|music\s*video)?|lyrics?)\s*$/i, " ");

const tidy = (s) => String(s || "")
  .replace(/\s+/g, " ")
  .replace(/^[\s\-–—|.~*]+|[\s\-–—|.~*]+$/g, "")
  .trim();

const deCamel = (s) => String(s || "")
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

const normWords = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Channel-ish artist detector — lyric/promo channels whose name is NOT the
// artist. Digits ("7clouds") or channel vocabulary both qualify.
const looksLikeChannel = (a) =>
  /\d/.test(a) || /vevo|official|music|tv$|lyrics|hype|records|media|promo|tube|network|channel|clouds|group/i.test(a);

// Split "Artist - Title" ONLY on a dash at bracket depth 0 — a dash inside
// "( فانكى - دقدق )" is part of the bracketed phrase, not a separator.
function splitTopLevelDash(s) {
  let depth = 0;
  const re = /\s[-–—]\s/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    depth = 0;
    for (let i = 0; i < m.index; i++) {
      const ch = s[i];
      if (ch === "(" || ch === "[" || ch === "{") depth++;
      else if (ch === ")" || ch === "]" || ch === "}") depth--;
    }
    if (depth <= 0) {
      return [s.slice(0, m.index), s.slice(m.index + m[0].length)];
    }
  }
  return [s];
}

function cleanYoutubeMetaOnce(rawTitle, rawArtist) {
  let title = tidy(stripDecorations(decodeEntities(rawTitle)));
  let artist = tidy(stripDecorations(decodeEntities(rawArtist)));

  // ── Artist channel forensics ────────────────────────────────────────
  const topic = artist.match(/^(.*)\s+-\s+Topic$/i);
  if (topic) artist = tidy(topic[1]);
  const vevo = artist.match(/^(.*?)VEVO$/i);
  if (vevo) artist = tidy(deCamel(vevo[1]));
  artist = tidy(artist.replace(/\s+(Official|Music|TV)$/i, ""));

  // ── Title "Artist - Title" handling (depth-0 dashes only) ───────────
  const parts = splitTopLevelDash(title);
  if (parts.length >= 2) {
    const a0 = tidy(stripTitleFeat(parts[0]));
    const rest = tidy(parts[1]);
    const na = normWords(artist);
    if (na && (normWords(a0) === na || normWords(a0).startsWith(na) || na.startsWith(normWords(a0)))) {
      // Redundant "Artist - " prefix — drop it, keep the known artist.
      title = rest;
    } else if (!na || na === "unknown") {
      artist = a0; title = rest;
    } else if (looksLikeChannel(artist)
      && !normWords(title).includes(na)) {
      // The artist field is a lyric/promo CHANNEL and the real artist is in
      // the title — promote it.
      artist = a0; title = rest;
    }
  }

  // ── Junk stripping on both fields ───────────────────────────────────
  title = tidy(stripTails(stripTitleFeat(stripJunkBrackets(title))));
  artist = tidy(stripJunkBrackets(artist));

  // Never return an empty field — fall back to the decoded original.
  if (!title) title = tidy(decodeEntities(rawTitle));
  if (!artist) artist = tidy(decodeEntities(rawArtist));
  return { title, artist };
}

// Run to a FIXPOINT (max 3 passes) so cleaning is idempotent by
// construction — a cleaned pair fed back in comes out identical, which the
// re-runnable cache script depends on.
function cleanYoutubeMeta(rawTitle, rawArtist) {
  let cur = { title: String(rawTitle || ""), artist: String(rawArtist || "") };
  for (let i = 0; i < 3; i++) {
    const next = cleanYoutubeMetaOnce(cur.title, cur.artist);
    if (next.title === cur.title && next.artist === cur.artist) break;
    cur = next;
  }
  return cur;
}
// Guest-PWA copy — MIRRORED in the desktop (launch-split/metadata-clean.js)
// and the cache script (reports-admin/scripts/clean-cache-metadata.js):
// change the rules in all three together.
export { cleanYoutubeMeta };
